import * as yup from 'yup';

const transformEmptyStringToUndefined = (value: any, originalValue: any) => {
  return originalValue === '' ? undefined : value;
};

export const MAX_SUB_PLANS = 5;

const urlPattern = /(https?:\/\/|www\.)\S+/i;
const emailPattern = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;

export type OfferType = 'cpl' | 'subscription';

export const createOfferSchema = (t: (key: string) => string, offerType: OfferType = 'cpl') => {
  const isSubscription = offerType === 'subscription';

  return yup.object({
    type: yup
      .string()
      .oneOf(['cpl', 'subscription'])
      .required(t('createOffer.validationErrors.offerTypeRequired')),
    source: yup.string().required(t('createOffer.validationErrors.sourceRequired')),
    subs: yup
      .array()
      .of(
        yup.object({
          // Для подписки поле "ge" (количество подписчиков) не используется —
          // цена задаётся за саму подписку, без ступеней.
          ge: isSubscription
            ? yup.number().transform(transformEmptyStringToUndefined).notRequired()
            : yup
                .number()
                .transform(transformEmptyStringToUndefined)
                .min(100, t('createOffer.validationErrors.subsGeMin'))
                .required(t('createOffer.validationErrors.subsGeRequired')),
          price: yup
            .number()
            .transform(transformEmptyStringToUndefined)
            .min(0.01, t('createOffer.validationErrors.subsPriceMin'))
            .required(t('createOffer.validationErrors.subsPriceRequired')),
        })
      )
      .min(1, t('createOffer.validationErrors.subsRequired'))
      .max(isSubscription ? 1 : MAX_SUB_PLANS, t('createOffer.validationErrors.subsRequired'))
      .required(t('createOffer.validationErrors.subsRequired')),
    subs_pre_day: yup
      .number()
      .transform(transformEmptyStringToUndefined)
      .min(10, t('createOffer.validationErrors.subsPerDayMin'))
      .required(t('createOffer.validationErrors.subsPerDayRequired')),
    // Блок "Всего подписчиков" скрыт для подписки — правила не применяются.
    subs_min: isSubscription
      ? yup.number().transform(transformEmptyStringToUndefined).notRequired()
      : yup
          .number()
          .transform(transformEmptyStringToUndefined)
          .min(100, t('createOffer.validationErrors.subsMinMin'))
          .required(t('createOffer.validationErrors.subsMinRequired')),
    subs_max: isSubscription
      ? yup.number().transform(transformEmptyStringToUndefined).notRequired()
      : yup
          .number()
          .transform(transformEmptyStringToUndefined)
          .min(100, t('createOffer.validationErrors.subsMaxMin'))
          .required(t('createOffer.validationErrors.subsMaxRequired'))
          .test(
            'max-greater-than-min',
            t('createOffer.validationErrors.subsMaxLessThanMin'),
            function (value) {
              const { subs_min } = this.parent;
              return value >= subs_min;
            }
          ),
    active_campaigns: yup
      .number()
      .transform(transformEmptyStringToUndefined)
      .min(1, t('createOffer.validationErrors.activeCampaignsMin'))
      .required(t('createOffer.validationErrors.activeCampaignsRequired')),
    description: yup
      .string()
      .min(10, t('createOffer.validationErrors.descriptionMin'))
      .test('description-no-links', t('createOffer.validationErrors.noLinksOrEmails'), (value) => {
        if (!value) return true;
        return !urlPattern.test(value) && !emailPattern.test(value);
      }),
    requirements: yup
      .string()
      .min(10, t('createOffer.validationErrors.requirementsMin'))
      .test('requirements-no-links', t('createOffer.validationErrors.noLinksOrEmails'), (value) => {
        if (!value) return true;
        return !urlPattern.test(value) && !emailPattern.test(value);
      })
      .required(t('createOffer.validationErrors.requirementsRequired')),
    campaign_types: yup
      .array()
      .of(yup.string())
      .min(1, t('createOffer.validationErrors.campaignTypesRequired')),
    // .required(t('createOffer.validationErrors.campaignTypesRequired')),
    trial_duration_from: yup.string().when('campaign_types', {
      is: (types: string[]) => types && types.includes('trial'),
      then: (schema) =>
        schema.required(t('createOffer.validationErrors.trialDurationFromRequired')),
      otherwise: (schema) => schema,
    }),
    trial_duration_to: yup.string().when('campaign_types', {
      is: (types: string[]) => types && types.includes('trial'),
      then: (schema) =>
        schema
          .required(t('createOffer.validationErrors.trialDurationToRequired'))
          .test(
            'trial-duration-order',
            t('createOffer.validationErrors.trialDurationOrder'),
            function (value) {
              const { trial_duration_from } = this.parent;
              if (!value || !trial_duration_from) return true;
              return Number(value) >= Number(trial_duration_from);
            }
          ),
      otherwise: (schema) => schema,
    }),
    show_romi: yup.boolean().required(),
    use_account_name_as_offer: yup.boolean().required(),
    other_source_name: yup.string().when('source', {
      is: 'another',
      then: (schema) => schema.required(t('createOffer.validationErrors.sourceNameRequired')),
      otherwise: (schema) => schema,
    }),
    status: yup.string().required(),
  });
};

export type CreateOfferFormData = yup.InferType<ReturnType<typeof createOfferSchema>>;
